import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import fetchData from '@/utils/fetchData';
import useError from '@/hooks/useError';

type Props = {
  children: ReactNode;
};

type ApplicationUpsale = {
  amount: number;
};

type RejectUpsaleResponse = {
  rejected: boolean;
};

type AcceptUpsaleResponse = {
  rejected: boolean;
};

type AcceptUpsaleOfferOptions = {
  body: {
    amount: number;
  };
};

export type ApplicationUpsaleContextType = {
  applicationUpsale: ApplicationUpsale | undefined;
  fetchApplicationUpsale: () => Promise<ApplicationUpsale | void>;
  acceptUpsaleOffer: ({
    body,
  }: AcceptUpsaleOfferOptions) => Promise<AcceptUpsaleResponse | void>;
  rejectUpsaleOffer: () => Promise<RejectUpsaleResponse | void>;
};

export const ApplicationUpsaleContext =
  createContext<ApplicationUpsaleContextType>(
    {} as ApplicationUpsaleContextType,
  );

export function ApplicationUpsaleProvider({
  children,
}: Props): React.JSX.Element {
  const [applicationUpsale, setApplicationUpsale] = useState<
    ApplicationUpsale | undefined
  >();
  const { showError } = useError();

  const fetchApplicationUpsale =
    useCallback(async (): Promise<ApplicationUpsale | void> => {
      try {
        const fetchedApplicationUpsale = await fetchData(
          '/client/application/upsale/display',
        );

        setApplicationUpsale(fetchedApplicationUpsale);
        return fetchedApplicationUpsale;
      } catch (e) {
        showError();
        throw e;
      }
    }, [showError]);

  const acceptUpsaleOffer = useCallback(
    async ({
      body,
    }: AcceptUpsaleOfferOptions): Promise<AcceptUpsaleResponse | void> => {
      const data = await fetchData('/client/application/upsale/accept', {
        method: 'post',
        body: JSON.stringify(body),
      });

      return data;
    },
    [],
  );

  const rejectUpsaleOffer =
    useCallback(async (): Promise<RejectUpsaleResponse | void> => {
      const data = await fetchData('/client/application/upsale/reject', {
        method: 'post',
      });
      return data;
    }, []);

  const ApplicationUpsaleContextValue = useMemo(
    () => ({
      applicationUpsale,
      fetchApplicationUpsale,
      acceptUpsaleOffer,
      rejectUpsaleOffer,
    }),
    [
      applicationUpsale,
      fetchApplicationUpsale,
      acceptUpsaleOffer,
      rejectUpsaleOffer,
    ],
  );

  return (
    <ApplicationUpsaleContext.Provider value={ApplicationUpsaleContextValue}>
      {children}
    </ApplicationUpsaleContext.Provider>
  );
}
